import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";

const API_URL = process.env.REACT_APP_API_URL;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const CheckHistory = () => {
  const auth = useAuth();
  const { activeCompany } = useCompany();
  const { t } = useTranslation();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchChecks = async () => {
      if (!auth || !auth.isAuthenticated || !activeCompany) { setLoading(false); return; }
      try {
        const token = auth.user?.access_token;
        const response = await fetch(`${API_URL}/checks/all?company_id=${activeCompany.company_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 404) {
          setChecks([]);
        } else if (!response.ok) {
          throw new Error(`Failed to fetch checks: ${response.status}`);
        } else {
          const data = await response.json();
          const sorted = [...data].sort(
            (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
          );
          setChecks(sorted);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChecks();
  }, [auth, activeCompany]);

  const handleSignOut = () => {
    auth.signOut();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return checks
      .filter((c) =>
        (c.payTo || "").toLowerCase().includes(q) ||
        (c.checkNumber || "").toString().includes(q) ||
        (c.date || "").includes(q) ||
        (c.memo || "").toLowerCase().includes(q)
      )
      .sort((a, b) => {
        let aVal = a[sortField] ?? "";
        let bVal = b[sortField] ?? "";
        if (sortField === "amount") {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
          return sortDir === "asc" ? aVal - bVal : bVal - aVal;
        }
        return sortDir === "asc"
          ? aVal.toString().localeCompare(bVal.toString())
          : bVal.toString().localeCompare(aVal.toString());
      });
  }, [checks, searchQuery, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const totalAmount = filtered.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const pageAmount = paginated.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1">⇅</span>;
    return <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, safePage - delta);
    const right = Math.min(totalPages, safePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push("…"); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push("…"); pages.push(totalPages); }
    return pages;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex flex-col flex-1 p-3 sm:p-6">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full">
          <h1 className="text-2xl font-semibold text-gray-800 mb-1">{t("check_history_title")}</h1>
          <p className="text-gray-500 mb-6 text-sm">{t("check_history_subtitle")}</p>

          {/* Controls row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
            <input
              type="text"
              placeholder={t("search_placeholder")}
              value={searchQuery}
              onChange={handleSearch}
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
              <label htmlFor="pageSize">{t("per_page")}</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={handlePageSizeChange}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10">
              <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-gray-400 text-sm">Loading check history…</span>
            </div>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : filtered.length === 0 ? (
            <p className="text-gray-500">
              {checks.length === 0 ? t("no_checks_found") : t("no_checks_match")}
            </p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600 uppercase text-xs">
                      <th className="px-4 py-3 border cursor-pointer select-none" onClick={() => handleSort("checkNumber")}>
                        {t("col_check_num")} <SortIcon field="checkNumber" />
                      </th>
                      <th className="px-4 py-3 border cursor-pointer select-none" onClick={() => handleSort("date")}>
                        {t("col_date")} <SortIcon field="date" />
                      </th>
                      <th className="px-4 py-3 border cursor-pointer select-none" onClick={() => handleSort("payTo")}>
                        {t("col_pay_to")} <SortIcon field="payTo" />
                      </th>
                      <th className="px-4 py-3 border cursor-pointer select-none" onClick={() => handleSort("amount")}>
                        {t("col_amount")} <SortIcon field="amount" />
                      </th>
                      <th className="px-4 py-3 border">{t("col_memo")}</th>
                      <th className="px-4 py-3 border">{t("col_actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((check) => (
                      <tr key={check.check_id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-2 border text-center">{check.checkNumber || "—"}</td>
                        <td className="px-4 py-2 border whitespace-nowrap">{check.date || "—"}</td>
                        <td className="px-4 py-2 border">{check.payTo || "—"}</td>
                        <td className="px-4 py-2 border text-right font-medium">${parseFloat(check.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 border text-gray-500 italic">{check.memo || "—"}</td>
                        <td className="px-4 py-2 border text-center">
                          <button className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-xs" onClick={() => setSelectedCheck(check)}>{t("view")}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold text-gray-700 text-sm">
                      <td className="px-4 py-2 border" colSpan={3}>Page total ({paginated.length} checks)</td>
                      <td className="px-4 py-2 border text-right">${pageAmount.toFixed(2)}</td>
                      <td className="px-4 py-2 border" colSpan={2} />
                    </tr>
                    <tr className="bg-gray-100 font-semibold text-gray-800 text-sm">
                      <td className="px-4 py-2 border" colSpan={3}>All results total ({filtered.length} {filtered.length === 1 ? "check" : "checks"})</td>
                      <td className="px-4 py-2 border text-right">${totalAmount.toFixed(2)}</td>
                      <td className="px-4 py-2 border" colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {paginated.map((check) => (
                  <div key={check.check_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <p className="font-semibold text-gray-800">{check.payTo || "—"}</p>
                        <p className="text-xs text-gray-500">{check.date || "—"} {check.checkNumber ? `· #${check.checkNumber}` : ""}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="font-bold text-gray-800">${parseFloat(check.amount || 0).toFixed(2)}</p>
                        <button className="mt-1 bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-xs" onClick={() => setSelectedCheck(check)}>{t("view")}</button>
                      </div>
                    </div>
                    {check.memo && <p className="text-xs text-gray-500 italic mt-1">{check.memo}</p>}
                  </div>
                ))}
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex justify-between text-sm font-semibold text-gray-700">
                  <span>Page total ({paginated.length})</span>
                  <span>${pageAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Pagination controls */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-2 text-sm text-gray-600">
                <span>
                  Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                    title="First page"
                  >
                    «
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                    title="Previous page"
                  >
                    ‹
                  </button>
                  {pageNumbers().map((p, i) =>
                    p === "…" ? (
                      <span key={`ellipsis-${i}`} className="px-2 py-1">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1 rounded border ${
                          p === safePage
                            ? "bg-green-500 text-white border-green-500"
                            : "border-gray-300 hover:bg-gray-100"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                    title="Next page"
                  >
                    ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                    title="Last page"
                  >
                    »
                  </button>
                </div>
              </div>
            </>
          )}

          <Link to="/" className="block mt-6">
            <button className="bg-gray-500 text-white py-2 px-6 rounded-lg hover:bg-gray-600 transition text-sm">
              {t("back_to_dashboard_btn")}
            </button>
          </Link>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {t("check_details_title", { number: selectedCheck.checkNumber || "N/A" })}
            </h2>
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
              <div><span className="font-medium">{t("col_date")}:</span> {selectedCheck.date}</div>
              <div><span className="font-medium">{t("col_amount")}:</span> ${parseFloat(selectedCheck.amount || 0).toFixed(2)}</div>
              <div className="col-span-2"><span className="font-medium">{t("col_pay_to")}:</span> {selectedCheck.payTo}</div>
              <div className="col-span-2"><span className="font-medium">{t("col_memo")}:</span> {selectedCheck.memo || "—"}</div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <p className="font-medium mb-1 text-gray-600">{t("account_holder")}</p>
                <p>{selectedCheck.accountHolderName}</p>
                <p>{selectedCheck.accountHolderAddress}</p>
                <p>{selectedCheck.accountHolderCity}, {selectedCheck.accountHolderState} {selectedCheck.accountHolderZip}</p>
              </div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <p className="font-medium mb-1 text-gray-600">{t("bank")}</p>
                <p>{selectedCheck.bankName}</p>
                <p>{t("routing")}: {selectedCheck.routingNumber}</p>
                <p>{t("account")}: ••••{(selectedCheck.bankAccountNumber || "").slice(-4)}</p>
              </div>
              {selectedCheck.created_at && (
                <div className="col-span-2 text-xs text-gray-400 pt-2 border-t">
                  {t("created")}: {new Date(selectedCheck.created_at).toLocaleString()}
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 text-sm"
                onClick={() => setSelectedCheck(null)}
              >
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}
      </Sidebar>
    </div>
  );
};

export default CheckHistory;
